<template>
  <div class="container-fluid mt-4" :class="themeClass">
    <div class="card shadow-sm">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <i class="bi bi-people me-2"></i>
          Listado de Clientes
        </h5>
        <router-link to="/clientes/nuevo" class="btn btn-primary">
          <i class="bi bi-plus-circle me-1"></i>
          Nuevo Cliente
        </router-link>
      </div>
      <div class="card-body">
        <!-- Mensaje de error -->
        <div v-if="error" class="alert alert-danger" role="alert">
          <i class="bi bi-exclamation-triangle me-2"></i>
          {{ error }}
          <button type="button" class="btn btn-sm btn-outline-danger ms-2" @click="loadClientes">
            <i class="bi bi-arrow-clockwise"></i> Reintentar
          </button>
        </div>

        <!-- Indicador de carga -->
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <p class="mt-2 text-muted">Cargando clientes...</p>
        </div>

        <!-- Contenido principal -->
        <div v-else>
          <!-- Filtros y búsqueda -->
          <div class="row mb-4">
            <div class="col-md-6">
              <div class="input-group">
                <span class="input-group-text">
                  <i class="bi bi-search"></i>
                </span>
                <input
                  v-model="searchTerm"
                  type="text"
                  class="form-control"
                  placeholder="Buscar por nombre, identificación..."
                />
              </div>
            </div>
            <div class="col-md-3">
              <select v-model="filterType" class="form-select">
                <option value="">Todos los tipos</option>
                <option v-for="tipo in tiposIdentificacion" :key="tipo.id" :value="tipo.codigo">
                  {{ tipo.tipo_de_identificacion }}
                </option>
              </select>
            </div>
            <div class="col-md-3">
              <select v-model="sortBy" class="form-select">
                <option value="razonSocial">Ordenar por nombre</option>
                <option value="identificacion">Ordenar por ID</option>
                <option value="fechaCreacion">Ordenar por fecha</option>
              </select>
            </div>
          </div>

          <!-- Tabla de clientes -->
          <div class="table-responsive">
            <table class="table table-hover">
              <thead class="table-light">
                <tr>
                  <th>Identificación</th>
                  <th>Nombre / Razón Social</th>
                  <th>Tipo ID</th>
                  <th>Teléfono</th>
                  <th>Email</th>
                  <th>Dirección</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="filteredClientes.length === 0">
                  <td colspan="6" class="text-center py-4 text-muted">
                    <i class="bi bi-info-circle me-2"></i>
                    No se encontraron clientes
                  </td>
                </tr>
                <tr v-else v-for="cliente in paginatedClientes" :key="cliente.id">
                  <td>{{ cliente.identificacion }}</td>
                  <td>{{ cliente.razonSocial }}</td>
                  <td>
                    <span class="badge" :class="getTipoBadgeClass(cliente.tipoId)">
                      {{ getTipoIdNombre(cliente.tipoId) }}
                    </span>
                  </td>
                  <td>{{ cliente.telefono || '-' }}</td>
                  <td>{{ cliente.correo || '-' }}</td>
                  <td>{{ cliente.direccion || '-' }}</td>
                  <td>
                    <div class="btn-group" role="group">
                      <button class="btn btn-sm btn-outline-primary" @click="verCliente(cliente)">
                        <i class="bi bi-eye"></i>
                      </button>
                      <button class="btn btn-sm btn-outline-warning" @click="editarCliente(cliente)">
                        <i class="bi bi-pencil"></i>
                      </button>
                      <button class="btn btn-sm btn-outline-danger" @click="mostrarConfirmacionEliminar(cliente)">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Paginación -->
          <nav v-if="totalPages > 1" aria-label="Paginación de clientes" class="mt-4">
            <ul class="pagination justify-content-center">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">Anterior</a>
              </li>
              <li
                v-for="page in totalPages"
                :key="page"
                class="page-item"
                :class="{ active: page === currentPage }"
              >
                <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">Siguiente</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <!-- Modal de Confirmación de Eliminación -->
    <div class="modal fade" id="confirmDeleteModal" tabindex="-1" aria-labelledby="confirmDeleteModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="confirmDeleteModalLabel">
              <i class="bi bi-exclamation-triangle text-danger me-2"></i>
              Confirmar Eliminación
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="mb-0">
              ¿Está seguro de que desea eliminar al cliente <strong>{{ clienteAEliminar?.razonSocial }}</strong>?
            </p>
            <p class="text-muted small mt-2">
              Esta acción no se puede deshacer.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              <i class="bi bi-x-lg me-1"></i>
              Cancelar
            </button>
            <button type="button" class="btn btn-danger" @click="confirmarEliminar" :disabled="eliminando">
              <i class="bi bi-trash me-1"></i>
              {{ eliminando ? 'Eliminando...' : 'Eliminar' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import themeManager from '@/store/theme'
import { clientService } from '@/services/clientService'
import api from '@/services/api'
import { Modal } from 'bootstrap'
import notificationService from '@/services/notificationService'

export default {
  name: 'ClientClientesListado',
  computed: {
    themeClass() {
      return themeManager.themeClass
    },
    filteredClientes() {
      let filtered = this.clientes
      if (this.searchTerm) {
        const t = this.searchTerm.toLowerCase()
        filtered = filtered.filter(c =>
          (c.razonSocial || '').toLowerCase().includes(t) ||
          (c.identificacion || '').includes(this.searchTerm) ||
          (c.correo || '').toLowerCase().includes(t)
        )
      }
      if (this.filterType) {
        filtered = filtered.filter(c => c.tipoId === this.filterType)
      }
      return filtered
    },
    paginatedClientes() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredClientes.slice(start, end)
    },
    totalPages() {
      return Math.ceil(this.filteredClientes.length / this.itemsPerPage)
    }
  },
  data() {
    return {
      tiposIdentificacion: [],
      searchTerm: '',
      filterType: '',
      sortBy: 'razonSocial',
      currentPage: 1,
      itemsPerPage: 25,
      clientes: [],
      loading: false,
      error: null,
      clienteAEliminar: null,
      eliminando: false
    }
  },
  async mounted() {
    this.fetchTiposIdentificacion()
    await this.loadClientes()
  },
  async activated() {
    // Recargar datos cuando se regrese a esta vista (desde edición, etc.)
    await this.loadClientes()
  },
  watch: {
    sortBy() {
      this.currentPage = 1
      this.loadClientes()
    },
    searchTerm() {
      this.currentPage = 1
    },
    filterType() {
      this.currentPage = 1
    },
  },
  methods: {
    async fetchTiposIdentificacion() {
      try {
        const res = await api.get('/tipos-identificacion-sri')
        this.tiposIdentificacion = res.data?.data || []
      } catch (e) {
        console.error('Error cargando tipos de identificación', e)
      }
    },

    async loadClientes() {
      try {
        this.loading = true
        this.error = null

        const params = {
          limit: 100,
          orden: this.mapSortField(this.sortBy),
          dir: 'ASC'
        }

        const response = await clientService.getClients(params)
        this.clientes = this.mapClientesData(response.data?.clientes || [])

      } catch (error) {
        console.error('Error loading clients:', error)
        this.error = 'Error al cargar los clientes'
        this.clientes = []
      } finally {
        this.loading = false
      }
    },

    mapClientesData(apiData) {
      return apiData.map(cliente => ({
        id: cliente.id,
        identificacion: cliente.identification_number,
        razonSocial: cliente.business_name,
        tipoId: cliente.tipo_identificacion || '',
        telefono: cliente.phone || '',
        correo: cliente.email || '',
        direccion: cliente.address || ''
      }))
    },

    mapSortField(sortBy) {
      const mapping = {
        'razonSocial': 'business_name',
        'identificacion': 'identification_number',
        'fechaCreacion': 'created_at'
      }
      return mapping[sortBy] || 'business_name'
    },

    getTipoIdNombre(codigo) {
      const tipo = this.tiposIdentificacion.find(t => t.codigo === codigo)
      return tipo ? tipo.tipo_de_identificacion : (codigo || '-')
    },

    getTipoBadgeClass(tipo) {
      const t = String(tipo ?? '').padStart(2, '0')
      if (t === '04') return 'bg-success'
      if (t === '05') return 'bg-primary'
      if (['06', '07', '08'].includes(t)) return 'bg-warning'
      return 'bg-secondary'
    },

    verCliente(cliente) {
      this.$router.push(`/clientes/ver/${cliente.id}`)
    },

    editarCliente(cliente) {
      this.$router.push(`/clientes/editar/${cliente.id}`)
    },

    mostrarConfirmacionEliminar(cliente) {
      this.clienteAEliminar = cliente
      // Mostrar modal usando Bootstrap
      const modal = new Modal(document.getElementById('confirmDeleteModal'))
      modal.show()
    },

    async confirmarEliminar() {
      if (!this.clienteAEliminar) return

      try {
        this.eliminando = true
        await clientService.deleteClient(this.clienteAEliminar.id)
        
        // Cerrar modal
        const modal = Modal.getInstance(document.getElementById('confirmDeleteModal'))
        modal.hide()
        
        // Recargar la lista
        await this.loadClientes()
        
        notificationService.success('Cliente eliminado exitosamente');
        
      } catch (error) {
        console.error('Error deleting client:', error)
        notificationService.error('Error al eliminar el cliente');
      } finally {
        this.eliminando = false
        this.clienteAEliminar = null
      }
    },

    mostrarNotificacion(mensaje, tipo) {
      if (tipo === 'success') notificationService.success(mensaje);
      else if (tipo === 'error') notificationService.error(mensaje);
      else if (tipo === 'warning') notificationService.warning(mensaje);
      else notificationService.info(mensaje);
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
      }
    }
  }
}
</script>

<style scoped>
/* Estilos para modo oscuro */
.dark-theme .card {
  background-color: var(--card-bg);
  border-color: var(--border-color);
  color: var(--text-primary);
}

.dark-theme .card-header {
  background-color: var(--card-header-bg);
  border-bottom-color: var(--border-color);
  color: var(--text-primary);
}

.dark-theme .table {
  color: var(--text-primary);
}

.dark-theme .table thead th {
  background-color: var(--table-header-bg);
  border-color: var(--border-color);
  color: var(--text-primary);
}

.dark-theme .table td {
  border-color: var(--border-color);
}

.dark-theme .table-hover tbody tr:hover {
  background-color: var(--table-hover-bg);
}

.dark-theme .form-control,
.dark-theme .form-select {
  background-color: var(--input-bg);
  border-color: var(--border-color);
  color: var(--text-primary);
}

.dark-theme .form-control:focus,
.dark-theme .form-select:focus {
  background-color: var(--input-bg);
  border-color: var(--primary-color);
  color: var(--text-primary);
}

.dark-theme .input-group-text {
  background-color: var(--input-bg);
  border-color: var(--border-color);
  color: var(--text-muted);
}

.dark-theme .btn-outline-primary {
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.dark-theme .btn-outline-primary:hover {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.dark-theme .btn-outline-warning {
  border-color: #f59e0b;
  color: #f59e0b;
}

.dark-theme .btn-outline-warning:hover {
  background-color: #f59e0b;
  border-color: #f59e0b;
}

.dark-theme .btn-outline-danger {
  border-color: #ef4444;
  color: #ef4444;
}

.dark-theme .btn-outline-danger:hover {
  background-color: #ef4444;
  border-color: #ef4444;
}

.dark-theme .pagination .page-link {
  background-color: var(--card-bg);
  border-color: var(--border-color);
  color: var(--text-primary);
}

.dark-theme .pagination .page-link:hover {
  background-color: var(--table-hover-bg);
  border-color: var(--border-color);
  color: var(--text-primary);
}

.dark-theme .pagination .page-item.active .page-link {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

/* Estilos específicos del componente */
.card {
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.dark-theme .card {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}

.card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0 !important;
  padding: 1.5rem;
}

.card-body {
  padding: 2rem;
}

.table {
  margin-bottom: 0;
}

.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.5px;
}

.table td {
  vertical-align: middle;
}

.btn-group .btn {
  margin-right: 0.25rem;
}

.btn-group .btn:last-child {
  margin-right: 0;
}

.badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.5rem;
}

.input-group-text {
  border-radius: 8px 0 0 8px;
}

.form-control,
.form-select {
  border-radius: 0 8px 8px 0;
}

@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .table-responsive {
    font-size: 0.875rem;
  }

  .btn-group {
    flex-direction: column;
  }

  .btn-group .btn {
    margin-right: 0;
    margin-bottom: 0.25rem;
  }
}
</style>


